import {
  GET_BUDGET_ITEMS_REQUEST,
  GET_BUDGET_ITEMS_SUCCESS,
  GET_BUDGET_ITEMS_FAILURE,
  GET_BUDGET_CATEGORIES_REQUEST,
  GET_BUDGET_CATEGORIES_SUCCESS,
  GET_BUDGET_CATEGORIES_FAILURE,
  CREATE_BUDGET_ITEM_REQUEST,
  CREATE_BUDGET_ITEM_SUCCESS,
  CREATE_BUDGET_ITEM_FAILURE,
  UPDATE_BUDGET_ITEM_REQUEST,
  UPDATE_BUDGET_ITEM_SUCCESS,
  UPDATE_BUDGET_ITEM_FAILURE,
  DELETE_BUDGET_ITEM_REQUEST,
  DELETE_BUDGET_ITEM_SUCCESS,
  DELETE_BUDGET_ITEM_FAILURE,
  SET_BUDGET_FILTERS,
  SET_BUDGET_LOADING,
  CLEAR_BUDGET_ERROR,
  CLEAR_BUDGET_CREATION_STATE,
  MARK_BUDGET_NOTIFICATION_SHOWN,
  GET_BUDGETS_REQUEST,
  GET_BUDGETS_SUCCESS,
  GET_BUDGETS_FAILURE,
  CREATE_BUDGET_REQUEST,
  CREATE_BUDGET_SUCCESS,
  CREATE_BUDGET_FAILURE,
  UPDATE_BUDGET_STATUS_REQUEST,
  UPDATE_BUDGET_STATUS_SUCCESS,
  UPDATE_BUDGET_STATUS_FAILURE,
  ACCEPT_BUDGET_REQUEST,
  ACCEPT_BUDGET_SUCCESS,
  ACCEPT_BUDGET_FAILURE,
  DELETE_BUDGET_REQUEST,
  DELETE_BUDGET_SUCCESS,
  DELETE_BUDGET_FAILURE,
  IBudgetAction
} from '../actions/budgetActions'
import { IBudgetItem, IBudgetCategory, IBudgetFilters, IBudgetItemsResponse, IBudgetCategoriesResponse } from '../types/budget'
import { IProjectBudgetsResponse } from '../../../api/budgetApi'

export interface IBudgetState {
  // Budget Items
  budgetItems: IBudgetItemsResponse | null
  currentBudgetItem: IBudgetItem | null
  
  // Budget Categories
  budgetCategories: IBudgetCategoriesResponse | null
  currentBudgetCategory: IBudgetCategory | null
  
  // Study Budgets (Project-level budgets)
  studyBudgets: IProjectBudgetsResponse | null
  studyBudgetsLoading: boolean
  studyBudgetsError: string | null
  lastCreatedBudget: any | null
  
  // UI State
  loading: boolean
  itemLoading: boolean
  categoryLoading: boolean
  error: string | null
  
  // Budget Creation State
  budgetCreationLoading: boolean
  budgetCreationSuccess: boolean
  budgetCreationError: string | null
  notificationShown: boolean

  // Filters
  filters: IBudgetFilters
  
  // Pagination
  currentPage: number
  totalPages: number
}

const initialState: IBudgetState = {
  budgetItems: null,
  currentBudgetItem: null,
  budgetCategories: null,
  currentBudgetCategory: null,
  studyBudgets: null,
  studyBudgetsLoading: false,
  studyBudgetsError: null,
  lastCreatedBudget: null,
  loading: false,
  itemLoading: false,
  categoryLoading: false,
  error: null,
  budgetCreationLoading: false,
  budgetCreationSuccess: false,
  budgetCreationError: null,
  notificationShown: false,
  filters: {},
  currentPage: 1,
  totalPages: 1
}

export const budgetReducer = (state = initialState, action: IBudgetAction): IBudgetState => {
  // Safety check for undefined actions
  if (!action || !action.type) {
    return state
  }
  
  switch (action.type) {
    // Budget Items
    case GET_BUDGET_ITEMS_REQUEST:
      return {
        ...state,
        itemLoading: true,
        error: null
      }
    
    case GET_BUDGET_ITEMS_SUCCESS:
      return {
        ...state,
        budgetItems: action.payload,
        itemLoading: false,
        error: null,
        currentPage: action.payload.current_page || 1,
        totalPages: action.payload.total_pages || 1
      }
    
    case GET_BUDGET_ITEMS_FAILURE:
      return {
        ...state,
        itemLoading: false,
        error: action.payload
      }

    // Budget Categories
    case GET_BUDGET_CATEGORIES_REQUEST:
      return {
        ...state,
        categoryLoading: true,
        error: null
      }
    
    case GET_BUDGET_CATEGORIES_SUCCESS:
      return {
        ...state,
        budgetCategories: action.payload,
        categoryLoading: false,
        error: null
      }
    
    case GET_BUDGET_CATEGORIES_FAILURE:
      return {
        ...state,
        categoryLoading: false,
        error: action.payload
      }

    // Create Budget Item
    case CREATE_BUDGET_ITEM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case CREATE_BUDGET_ITEM_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        budgetItems: state.budgetItems ? {
          ...state.budgetItems,
          results: [action.payload, ...state.budgetItems.results],
          count: state.budgetItems.count + 1
        } : null
      }
    
    case CREATE_BUDGET_ITEM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    // Update Budget Item
    case UPDATE_BUDGET_ITEM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case UPDATE_BUDGET_ITEM_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        budgetItems: state.budgetItems ? {
          ...state.budgetItems,
          results: state.budgetItems.results.map(item => 
            item.id === action.payload.id ? action.payload : item
          )
        } : null,
        currentBudgetItem: action.payload
      }
    
    case UPDATE_BUDGET_ITEM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    // Delete Budget Item
    case DELETE_BUDGET_ITEM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case DELETE_BUDGET_ITEM_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        budgetItems: state.budgetItems ? {
          ...state.budgetItems,
          results: state.budgetItems.results.filter(item => item.id !== action.payload),
          count: state.budgetItems.count - 1
        } : null
      }
    
    case DELETE_BUDGET_ITEM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    // Filters and UI
    case SET_BUDGET_FILTERS:
      return {
        ...state,
        filters: action.payload
      }
    
    case SET_BUDGET_LOADING:
      return {
        ...state,
        loading: action.payload
      }
    
    case CLEAR_BUDGET_ERROR:
      return {
        ...state,
        error: null,
        studyBudgetsError: null
      }
    
    case CLEAR_BUDGET_CREATION_STATE:
      return {
        ...state,
        error: null,
        studyBudgetsError: null,
        lastCreatedBudget: null,
        budgetCreationLoading: false,
        budgetCreationSuccess: false,
        budgetCreationError: null,
        notificationShown: false,
        loading: false
      }

    case MARK_BUDGET_NOTIFICATION_SHOWN:
      return {
        ...state,
        notificationShown: true
      }

    // Study Budgets (Project-level budgets)
    case GET_BUDGETS_REQUEST:
      return {
        ...state,
        studyBudgetsLoading: true,
        studyBudgetsError: null
      }
    
    case GET_BUDGETS_SUCCESS:
      return {
        ...state,
        studyBudgets: action.payload,
        studyBudgetsLoading: false,
        studyBudgetsError: null
      }
    
    case GET_BUDGETS_FAILURE:
      return {
        ...state,
        studyBudgetsLoading: false,
        studyBudgetsError: action.payload
      }

    case CREATE_BUDGET_REQUEST:
      // Create optimistic budget entry
      const optimisticBudget = {
        id: `optimistic_${Date.now()}`,
        project_id: `optimistic_project_${Date.now()}`,
        name: 'New Clinical Study',
        client: 'Creating...', // Use 'client' field as per IProjectBudget interface
        project_type: 'sponsor' as const,
        status: 'draft' as const,
        health: 'on_track' as const,
        start_date: new Date().toISOString(),
        end_date: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        created_at: new Date().toISOString(),
        responsible_person: 'Current User',
        department: 'Clinical Research',
        priority: 'medium' as const,
        notes: 'Creating budget...',
        total_budget: 0,
        allocated_budget: 0,
        spent_amount: 0,
        remaining_budget: 0,
        data_completeness_percentage: 0,
        category: 'General',
        _isOptimistic: true
      }
      
      return {
        ...state,
        loading: true,
        error: null,
        budgetCreationLoading: true,
        budgetCreationSuccess: false,
        budgetCreationError: null,
        lastCreatedBudget: null,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          results: [optimisticBudget, ...state.studyBudgets.results],
          count: state.studyBudgets.count + 1
        } : {
          results: [optimisticBudget],
          count: 1,
          links: { next: null, previous: null },
          page_size: 10,
          current_page: 1,
          total_pages: 1,
          to: 1,
          from: 1,
          last_page: 1
        }
      }
    
    case CREATE_BUDGET_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        budgetCreationLoading: false,
        budgetCreationSuccess: true,
        budgetCreationError: null,
        lastCreatedBudget: action.payload,
        notificationShown: false,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          // Remove optimistic budget and add the real one
          results: [
            action.payload,
            ...state.studyBudgets.results.filter(budget => !(budget as any)._isOptimistic)
          ],
          count: state.studyBudgets.count // Keep same count since we're replacing optimistic with real
        } : {
          // BUGFIX: Create studyBudgets structure if it doesn't exist
          results: [action.payload],
          count: 1,
          links: { next: null, previous: null },
          page_size: 10,
          current_page: 1,
          total_pages: 1,
          to: 1,
          from: 1,
          last_page: 1
        }
      }
    
    case CREATE_BUDGET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        budgetCreationLoading: false,
        budgetCreationSuccess: false,
        budgetCreationError: action.payload,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          // Remove optimistic budget on failure
          results: state.studyBudgets.results.filter(budget => !(budget as any)._isOptimistic),
          count: state.studyBudgets.count - 1
        } : null
      }

    case UPDATE_BUDGET_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case UPDATE_BUDGET_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          results: state.studyBudgets.results.map(budget => 
            budget.id === action.payload.id ? action.payload : budget
          )
        } : null
      }
    
    case UPDATE_BUDGET_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    case ACCEPT_BUDGET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }

    case ACCEPT_BUDGET_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          results: state.studyBudgets.results.map(budget =>
            budget.id === action.payload.id ? action.payload : budget
          )
        } : null
      }

    case ACCEPT_BUDGET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    case DELETE_BUDGET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case DELETE_BUDGET_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        studyBudgets: state.studyBudgets ? {
          ...state.studyBudgets,
          results: state.studyBudgets.results.filter(budget => budget.id !== action.payload),
          count: state.studyBudgets.count - 1
        } : null
      }
    
    case DELETE_BUDGET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      }

    default:
      return state
  }
} 